import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Papa } from 'ngx-papaparse';
import { CommonEntityService } from 'src/app/common/services/common-entity.service';
import { DataService } from 'src/app/services/data.service';
import { MessageService } from 'primeng/api';

interface ImportRow {
  question: string;
  minigame_type?: string;
  creatureType_ref?: string;
  variant_ref?: string;
  stages?: string;
  category?: string;
  icon_ref?: string;
  enabled?: string;
  env?: string;
  correct_answer?: string;
  incorrect_answer_1?: string;
  incorrect_answer_2?: string;
  incorrect_answer_3?: string;
  incorrect_answer_4?: string;
  [key: string]: any;
}

interface ResolvedAnswer {
  title: string;
  correct: boolean;
}

interface ResolvedRow {
  rowNum: number;
  question: string;
  payload: any | null;
  answers: ResolvedAnswer[];
  warnings: string[];
}

interface RowResult {
  row: number;
  question: string;
  status: 'success' | 'error' | 'skipped';
  message?: string;
}

type Step = 'upload' | 'preview' | 'results';

@Component({
  selector: 'app-questions-bulk-import',
  templateUrl: './questions-bulk-import.component.html',
  styleUrls: ['./questions-bulk-import.component.sass'],
  providers: [MessageService],
})
export class QuestionsBulkImportComponent {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  step: Step = 'upload';
  parsedRows: ImportRow[] = [];
  resolvedRows: ResolvedRow[] = [];
  results: RowResult[] = [];
  isLoading: boolean = false;
  isImporting: boolean = false;
  progress: string = '';
  pendingImportConfirm: boolean = false;

  constructor(
    private papa: Papa,
    private commonEntityService: CommonEntityService,
    private dataService: DataService,
    private messageService: MessageService,
  ) {}

  onFileChange(event: any) {
    const file = event.target.files[0];
    if (!file) return;
    this.reset();

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.papa.parse(e.target.result, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (res: any) => {
          const rows: ImportRow[] = res.data;
          if (!rows.length || !('question' in rows[0])) {
            this.messageService.add({ severity: 'error', summary: 'Invalid CSV', detail: 'CSV must have a "question" column.' });
            return;
          }
          this.parsedRows = rows;
        },
        error: (err: any) => {
          this.messageService.add({ severity: 'error', summary: 'Parse Error', detail: err.message });
        },
      });
    };
    reader.readAsText(file);
  }

  async preview() {
    if (!this.parsedRows.length) return;
    this.isLoading = true;

    const [miniGameTypes, creatureTypes, variants, stages, categories, icons] = await Promise.all([
      this.dataService.getAllOfTypeAsync('minigame-types',      { query: {}, select: 'name _id' }).catch(() => []),
      this.dataService.getAllOfTypeAsync('creaturetypes',       { query: {}, select: 'name _id' }).catch(() => []),
      this.dataService.getAllOfTypeAsync('variants',            { query: {}, select: 'name _id' }).catch(() => []),
      this.dataService.getAllOfTypeAsync('stages',              { query: {}, select: 'internalName _id' }).catch(() => []),
      this.dataService.getAllOfTypeAsync('need-categories',     { query: {}, select: 'name _id' }).catch(() => []),
      this.dataService.getAllOfTypeAsync('miscellaneous-build', { query: {}, select: 'name _id' }).catch(() => []),
    ]);

    const byName = (arr: any[], key = 'name') =>
      new Map<string, string>((arr || []).map((r: any) => [r[key]?.toLowerCase(), r._id]));

    const mgTypeMap   = byName(miniGameTypes);
    const ctMap       = byName(creatureTypes);
    const varMap      = byName(variants);
    const stageMap    = byName(stages, 'internalName');
    const categoryMap = byName(categories);
    const iconMap     = byName(icons);

    this.resolvedRows = this.parsedRows.map((row, i) => {
      const payload: any = {};
      const answers: ResolvedAnswer[] = [];
      const warnings: string[] = [];

      if (!row.question?.trim()) {
        return { rowNum: i + 1, question: '', payload: null, answers: [], warnings: ['Missing question text'] };
      }

      payload.question = row.question.trim();

      const resolveRef = (val: string | undefined, map: Map<string, string>, field: string) => {
        if (!val?.trim()) return;
        const id = map.get(val.trim().toLowerCase());
        if (id) payload[field] = id;
        else warnings.push(`${field}: "${val}" not found`);
      };

      resolveRef(row.minigame_type,    mgTypeMap,   'minigame_type');
      resolveRef(row.creatureType_ref, ctMap,       'creatureType_ref');
      resolveRef(row.variant_ref,      varMap,      'variant_ref');
      resolveRef(row.category,         categoryMap, 'category');
      resolveRef(row.icon_ref,         iconMap,     'icon_ref');

      if (row.stages?.trim()) {
        const names = row.stages.split('|').map((s: string) => s.trim()).filter(Boolean);
        const ids = names.map((n: string) => {
          const id = stageMap.get(n.toLowerCase());
          if (!id) warnings.push(`stages: "${n}" not found`);
          return id;
        }).filter(Boolean);
        if (ids.length) payload.stages = ids;
      }

      payload.enabled = row.enabled?.trim().toLowerCase() === 'false' ? false : true;

      if (row.env?.trim()) {
        payload.env = row.env.split(',').map((e: string) => e.trim()).filter(Boolean);
      }

      // Collect answer titles from CSV — resolved to IDs at import time
      if (row.correct_answer?.trim()) {
        answers.push({ title: row.correct_answer.trim(), correct: true });
      }
      for (let n = 1; n <= 4; n++) {
        const val = row[`incorrect_answer_${n}`]?.trim();
        if (val) answers.push({ title: val, correct: false });
      }

      return { rowNum: i + 1, question: row.question, payload, answers, warnings };
    });

    this.isLoading = false;
    this.step = 'preview';
  }

  get warnRowNums(): number[] {
    return this.resolvedRows.filter(r => r.payload && r.warnings.length).map(r => r.rowNum);
  }

  import() {
    if (this.warnRowNums.length && !this.pendingImportConfirm) {
      this.pendingImportConfirm = true;
      return;
    }
    this.pendingImportConfirm = false;
    this.doImport();
  }

  cancelImportConfirm() {
    this.pendingImportConfirm = false;
  }

  private async doImport() {
    if (!this.resolvedRows.length) return;
    this.isImporting = true;
    this.results = [];

    for (let i = 0; i < this.resolvedRows.length; i++) {
      const { rowNum, question, payload, answers, warnings } = this.resolvedRows[i];
      this.progress = `${i + 1} / ${this.resolvedRows.length}`;

      if (!payload) {
        this.results.push({ row: rowNum, question, status: 'skipped', message: 'Missing question text' });
        continue;
      }

      try {
        // Look up existing answers by title — never create new ones
        // Schema: correct = single ObjectId, incorrect = array of ObjectIds
        if (answers.length) {
          const correctAns = answers.find(a => a.correct);
          const incorrectAns = answers.filter(a => !a.correct);

          const lookupAnswer = async (title: string): Promise<string | null> => {
            const res = await this.dataService.findManyDynamic('answers', {
              query: { title },
              select: '_id',
              pageSize: 1,
            });
            const list = Array.isArray(res) ? res : (res?.records ?? res?.data ?? []);
            if (!list.length) { warnings.push(`Answer not found: "${title}"`); return null; }
            return list[0]._id;
          };

          if (correctAns) {
            const id = await lookupAnswer(correctAns.title);
            if (id) payload.correct = id;
          }

          if (incorrectAns.length) {
            const ids = (await Promise.all(incorrectAns.map(a => lookupAnswer(a.title)))).filter(Boolean);
            if (ids.length) payload.incorrect = ids;
          }
        }

        const created = await this.commonEntityService.createDynamicEntity('questions', payload);
        this.results.push({
          row: rowNum, question, status: 'success',
          message: warnings.length ? `Warnings: ${warnings.join('; ')}` : (created?.id ? `ID: ${created.id}` : undefined),
        });
      } catch (e: any) {
        this.results.push({ row: rowNum, question, status: 'error', message: e?.error?.message || 'Failed to create' });
      }
    }

    this.isImporting = false;
    this.progress = '';
    this.step = 'results';
  }

  getAnswerTitles(answers: ResolvedAnswer[], correct: boolean): string {
    return answers.filter(a => a.correct === correct).map(a => a.title).join(' | ');
  }

  get validRowCount()   { return this.resolvedRows.filter(r => r.payload).length; }
  get warnRowCount()    { return this.resolvedRows.filter(r => r.payload && r.warnings.length).length; }
  get invalidRowCount() { return this.resolvedRows.filter(r => !r.payload).length; }
  get successCount()    { return this.results.filter(r => r.status === 'success').length; }
  get errorCount()      { return this.results.filter(r => r.status === 'error').length; }
  get skippedCount()    { return this.results.filter(r => r.status === 'skipped').length; }

  downloadTemplate() {
    const headers = 'question,correct_answer,incorrect_answer_1,incorrect_answer_2,incorrect_answer_3,incorrect_answer_4,minigame_type,creatureType_ref,variant_ref,stages,category,icon_ref,enabled,env';
    const example = '"What color is the sky?","Blue","Red","Green","Yellow","Purple",Trivia Trail,Butterfly,Monarch Butterfly,Stage1|Stage2,Hunger,,true,"dev,qa,prod"';
    const blob = new Blob([headers + '\n' + example], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  back() { this.step = 'upload'; this.resolvedRows = []; this.pendingImportConfirm = false; }

  close() {
    this.visibleChange.emit(false);
    this.reset();
  }

  private reset() {
    this.step = 'upload';
    this.parsedRows = [];
    this.resolvedRows = [];
    this.results = [];
    this.progress = '';
    this.pendingImportConfirm = false;
  }
}
